import React, { useState } from "react";
import api from "../lib/api";
import { KeyRound, ShieldOff, ShieldCheck, Clock, X } from "lucide-react";

const PRESETS = [
  { key: "30_days", label: "30 dias" },
  { key: "60_days", label: "60 dias" },
  { key: "1_year", label: "1 ano" },
  { key: "2_years", label: "2 anos" },
  { key: "lifetime", label: "Vitalício" },
];

function DurationPicker({ open, onClose, title, subtitle, ctaLabel, onConfirm, required }) {
  const [preset, setPreset] = useState("30_days");
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[#14161C] border border-[#282D3D] rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-extrabold text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        {subtitle && <p className="text-xs text-gray-300 mb-3">{subtitle}</p>}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 mb-4">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              data-testid={`duration-${p.key}`}
              onClick={() => setPreset(p.key)}
              className={`px-2 py-2 text-[11px] font-bold rounded-lg border transition-colors ${
                preset === p.key
                  ? "border-[#FF6B00] bg-[#FF6B00]/15 text-[#FF6B00]"
                  : "border-[#282D3D] bg-[#0F1219] text-gray-300 hover:border-gray-500"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          data-testid="duration-confirm"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onConfirm(preset);
              onClose();
            } finally {
              setSaving(false);
            }
          }}
          className="w-full py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider disabled:opacity-60"
        >
          {saving ? "Confirmando..." : ctaLabel}
        </button>
      </div>
    </div>
  );
}

export default function AccessControls({ student, onRefresh }) {
  const [approveOpen, setApproveOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [unblockOpen, setUnblockOpen] = useState(false);
  const [message, setMessage] = useState("");

  const access = student.access || {};
  const effective = access.effective_status || "no_membership";
  const isLifetime = !!access.is_lifetime;
  const expiresAt = access.access_expires_at;
  const membershipId = student.membership_id;

  const call = async (path, body) => {
    if (!membershipId) return alert("Aluno sem vínculo de mentoria.");
    try {
      await api.post(`/api/memberships/${membershipId}${path}`, body);
      setMessage(
        body?.preset === "lifetime"
          ? "Acesso vitalício."
          : "Acesso liberado com sucesso."
      );
      onRefresh && onRefresh();
      setTimeout(() => setMessage(""), 4000);
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro ao atualizar acesso.");
    }
  };

  const statusColor =
    {
      active: "bg-emerald-500/20 border-emerald-500/40 text-emerald-300",
      pending: "bg-yellow-500/20 border-yellow-500/40 text-yellow-200",
      expired: "bg-red-500/20 border-red-500/40 text-red-300",
      blocked: "bg-gray-600/40 border-gray-500 text-gray-200",
      no_membership: "bg-gray-700/40 border-gray-600 text-gray-300",
    }[effective] || "bg-gray-700/40 border-gray-600 text-gray-300";

  const labelMap = {
    active: isLifetime ? "Vitalício" : "Ativo",
    pending: "Aguardando aprovação",
    expired: "Expirado",
    blocked: "Bloqueado",
    no_membership: "Sem vínculo",
  };

  return (
    <div data-testid={`access-controls-${student.id}`} className="flex flex-wrap items-center gap-1.5">
      <span
        data-testid={`access-status-${student.id}`}
        className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-lg border ${statusColor}`}
      >
        {labelMap[effective] || effective}
        {effective === "active" && !isLifetime && expiresAt && (
          <span className="ml-1 font-normal opacity-80">
            até {new Date(expiresAt).toLocaleDateString("pt-BR")}
          </span>
        )}
      </span>

      {effective === "pending" && (
        <button
          data-testid={`approve-btn-${student.id}`}
          onClick={(e) => { e.stopPropagation(); setApproveOpen(true); }}
          className="text-[10px] font-extrabold uppercase px-2 py-1 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black"
        >
          Aprovar Acesso
        </button>
      )}
      {effective === "active" && (
        <>
          <button
            data-testid={`renew-btn-${student.id}`}
            onClick={(e) => { e.stopPropagation(); setRenewOpen(true); }}
            className="text-[10px] font-extrabold uppercase px-2 py-1 rounded-lg bg-[#1F2330] border border-[#3E4760] hover:bg-[#282F42] text-white"
          >
            Renovar
          </button>
          <button
            data-testid={`change-duration-btn-${student.id}`}
            onClick={(e) => { e.stopPropagation(); setChangeOpen(true); }}
            className="text-[10px] font-extrabold uppercase px-2 py-1 rounded-lg bg-[#1F2330] border border-[#3E4760] hover:bg-[#282F42] text-white"
          >
            Alterar prazo
          </button>
          <button
            data-testid={`block-btn-${student.id}`}
            onClick={async (e) => {
              e.stopPropagation();
              if (!window.confirm("Bloquear o acesso deste aluno?")) return;
              await call("/block", {});
            }}
            className="text-[10px] font-extrabold uppercase px-2 py-1 rounded-lg bg-red-600 hover:bg-red-500 text-white flex items-center gap-1"
          >
            <ShieldOff className="w-3 h-3" /> Bloquear
          </button>
        </>
      )}
      {effective === "expired" && (
        <button
          data-testid={`renew-expired-btn-${student.id}`}
          onClick={(e) => { e.stopPropagation(); setRenewOpen(true); }}
          className="text-[10px] font-extrabold uppercase px-2 py-1 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black"
        >
          Renovar acesso
        </button>
      )}
      {effective === "blocked" && (
        <button
          data-testid={`unblock-btn-${student.id}`}
          onClick={(e) => { e.stopPropagation(); setUnblockOpen(true); }}
          className="text-[10px] font-extrabold uppercase px-2 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black flex items-center gap-1"
        >
          <ShieldCheck className="w-3 h-3" /> Liberar
        </button>
      )}

      {message && (
        <span className="text-[10px] text-emerald-300 font-bold ml-1">{message}</span>
      )}

      <DurationPicker
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        title="Aprovar acesso do aluno"
        subtitle="Por quanto tempo este aluno terá acesso?"
        ctaLabel="LIBERAR ACESSO"
        onConfirm={(preset) => call("/approve", { preset })}
      />
      <DurationPicker
        open={renewOpen}
        onClose={() => setRenewOpen(false)}
        title="Renovar acesso"
        subtitle="Por quanto tempo deseja renovar o acesso?"
        ctaLabel="CONFIRMAR RENOVAÇÃO"
        onConfirm={(preset) => call("/renew", { preset })}
      />
      <DurationPicker
        open={changeOpen}
        onClose={() => setChangeOpen(false)}
        title="Alterar prazo de acesso"
        subtitle="Escolha a nova duração a partir de hoje."
        ctaLabel="APLICAR NOVO PRAZO"
        onConfirm={(preset) => call("/change-duration", { preset })}
      />
      <DurationPicker
        open={unblockOpen}
        onClose={() => setUnblockOpen(false)}
        title="Liberar acesso"
        subtitle="Selecione a nova duração para liberar o acesso deste aluno."
        ctaLabel="LIBERAR ACESSO"
        onConfirm={(preset) => call("/unblock", { preset })}
      />
    </div>
  );
}
